/* eslint-disable no-restricted-syntax */
// https://stackoverflow.com/a/53182678

import { XMLParser } from 'fast-xml-parser';

import { activeLanes } from '../../constants';
import { EventTypeIdentifier, EventTypePalette } from '../../enums';
import { DecodedKML, Folder, KMLEntry, ParsedRouteDefinition, Placemark, RootFolder } from '../../interfaces';

export const parseLocalKMLFile = (file: any): Promise<ParsedRouteDefinition> => {
  const fileReader = new FileReader();

  return new Promise((resolve, reject) => {
    fileReader.onload = async (e: any) => {
      const result = extractGoogleCoords(e.target.result);
      resolve(result);
    };
    fileReader.readAsText(file);
  });
};

export const extractGoogleCoords = (plainText: string): ParsedRouteDefinition => {
  const googlePolygons: ParsedRouteDefinition['polygons'] = [];
  const googleMarkers: ParsedRouteDefinition['markers'] = [];

  // Helper function to check if a placemark should be ignored
  const shouldIgnorePlacemark = (name: string): boolean => {
    // Check if the name is any of R0-R10
    return /^R([0-9]|10)$/.test(name);
  };

  const addLine = (blockName: string, name: string, coordinates: string, block: any) => {
    // Skip processing if this is a lane line marker
    if (shouldIgnorePlacemark(name)) {
      return;
    }

    const parsedIntervals: { enforce_start: number; enforce_end: number; enforce_days: number[] }[] = [];
    // supporting two formats:
    // Bracket format: BusLane|[7.0,9.0,ALL][16.0,19.0,0123456]|RD
    // Flat format: BusLane|7.0,9.0,16.0,19.0|12345|RD

    const segments = name.split('|');
    const eventTypeAndLocation = segments[0];
    const activeLane = segments.pop();

    // Bracket format: BusLane|[7.0,9.0,ALL][16.0,19.0,0123456]|RD
    {
      const [_, enforceTimeAndDays = ''] = name.split('|');
      for (const match of enforceTimeAndDays.matchAll(/\[([^\]]+)\]/g)) {
        const parts = match[1].split(',');
        if (parts.length < 3) continue;
        const enforce_start = parseFloat(parts[0]);
        const enforce_end = parseFloat(parts[1]);
        if (!Number.isFinite(enforce_start) || !Number.isFinite(enforce_end)) continue;
        const daysStr = parts[2].replace(/\D/g, '');
        parsedIntervals.push({
          enforce_start,
          enforce_end,
          enforce_days: parts[2] === 'ALL' ? [0, 1, 2, 3, 4, 5, 6] : daysStr.split('').map(Number),
        });
      }
    }

    // Flat format: BusLane|7.0,9.0,16.0,19.0|12345|RD — pairs of start,end with no days (defaults to ALL)
    {
      const [_, enforceTimes = '', enforceDays = ''] = name.split('|');

      if (parsedIntervals.length === 0 && enforceTimes && !enforceTimes.includes('[')) {
        const nums = enforceTimes.split(',').map(parseFloat);
        for (let i = 0; i + 1 < nums.length; i += 2) {
          const enforce_start = nums[i];
          const enforce_end = nums[i + 1];
          if (!Number.isFinite(enforce_start) || !Number.isFinite(enforce_end)) {
            continue;
          }
          const daysStr = enforceDays.replace(/\D/g, '');

          const enforce_days = enforceDays === 'ALL' ? [0, 1, 2, 3, 4, 5, 6] : daysStr.split('').map(Number);

          parsedIntervals.push({ enforce_start, enforce_end, enforce_days });
        }
      }
    }

    if (!coordinates) {
      return;
    }

    // #SB|Wyckoff Ave @ Cornelia St
    const route_direction = block.Placemark?.name?.split('|')[0].replace('#', '');
    const blockNameIntersection = block.Placemark?.name?.split('|')[1] || blockName;

    const regexpWithOptionalLocationID = /(\w+)(?:\[#(\d+)\])?/;
    const query2 = eventTypeAndLocation.match(regexpWithOptionalLocationID);
    const eventType = query2?.[1] ?? '';
    const bus_stop_id = query2?.[2]; // optional matching group

    const enforcement_info: KMLEntry['enforcement_info'] = [];
    const active_lane_selection_acronyms = activeLane.split(',');
    const active_lane_selection = activeLanes.filter((lane) => active_lane_selection_acronyms.includes(lane.acronym)).map((l) => l.id);

    const mapEventType = (kmlEventType: string): EventTypeIdentifier => {
      // Maps routefiles' BusLaneMoving to ETI BUS_LANE_MOVING
      return kmlEventType.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase() as EventTypeIdentifier;
    };

    const eventTypeColors = (et: EventTypeIdentifier): string => {
      return EventTypePalette[et] ?? '#FF00FF';
    };

    const event_types = eventType.split(',').map(mapEventType);

    for (const { enforce_start, enforce_end, enforce_days } of parsedIntervals) {
      enforcement_info.push({
        id: Math.random(),
        event_types,
        enforce_start,
        enforce_end,
        enforce_days,
        active_lane_selection,
        block_name: blockNameIntersection,
      });
    }

    const data: Partial<KMLEntry> = {
      event_types,
      bus_stop_id,
      active_lane_selection,
      enforcement_info,
      block_name: blockNameIntersection,
      route_direction,
    };

    const points = coordinates.split(' ');

    const googlePolygonsPaths: ParsedRouteDefinition['polygons'][0]['path'] = [];
    for (const point of points) {
      const coord = point.split(',');
      googlePolygonsPaths.push({ lat: +coord[1], lng: +coord[0] });
    }

    googlePolygons.push({
      path: googlePolygonsPaths,
      color: eventTypeColors(data.event_types[0]),
      data,
      type: data.event_types[0],
    });
  };

  const parseBlock = (block: Folder | RootFolder) => {
    let placemark: Placemark | Placemark[] | undefined;

    if (Array.isArray(block.Folder)) {
      const enforcementZone = block.Folder.find((f) => f.name === 'EnforceZones');
      placemark = enforcementZone?.Placemark;
    } else if (block.Folder?.Placemark) {
      placemark = block.Folder?.Placemark;
    } else {
      placemark = block.Placemark;
    }

    if (Array.isArray(placemark)) {
      placemark.forEach((p) => {
        if (!shouldIgnorePlacemark(p.name)) {
          addLine(block.name, p.name, p.LineString?.coordinates, block);
        }
      });
    } else if (placemark && !shouldIgnorePlacemark(placemark.name)) {
      addLine(block.name, placemark.name, placemark.LineString?.coordinates, block);
    }
  };

  // Recursively process all folders to find and process leaf nodes
  const processFolder = (folder: Folder | RootFolder) => {
    // Skip processing if this is a LaneLines folder
    if (folder.name === 'LaneLines') {
      return;
    }

    // Process this folder if it has placemarks
    if (folder.Placemark) {
      parseBlock(folder);
    } else if (folder.Folder) {
      // Continue traversing if there are nested folders
      if (Array.isArray(folder.Folder)) {
        folder.Folder.forEach(processFolder);
      } else {
        processFolder(folder.Folder);
      }
    }
  };

  const xmlparser = new XMLParser();
  const jObj: DecodedKML = xmlparser.parse(plainText);

  // support for old file format
  if (jObj.kml.Document?.Placemark) {
    parseBlock(jObj.kml.Document);
  }

  // support for new file format with recursive folder traversal
  if (jObj.kml.Document?.Folder) {
    if (Array.isArray(jObj.kml.Document.Folder)) {
      jObj.kml.Document.Folder.forEach(processFolder);
    } else {
      processFolder(jObj.kml.Document.Folder);
    }
  }

  return { markers: googleMarkers, polygons: googlePolygons };
};
